import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, generatePath } from 'react-router-dom'

import { Tooltip } from 'antd'
import { colours } from '@qflow/theme'

import { Flex, Text, icons, ColourButton, Spacer } from 'portal/lib/primitives'
import { useModuleIds, useUserProjectRole } from 'portal/lib/hooks'
import { sizes } from '@qflow/theme'

import { DELIVERY_EVENTS, WASTE_EVENTS } from 'portal/routes'
import { datePickerChanged } from 'portal/store/global/actions'
import { selectGlobalDateISO } from 'portal/store/global/selectors'
import { queueExport } from 'portal/store/records/exports/actions'
import { OneClickExportButton } from './OneClickExportButton'
export function RecordsExport({
  title,
  startDate,
  endDate,
  filters,
  trackDataExported
}) {
  const { working, error } = useSelector(state => state.records.exports)
  const isOneClickLcaEnabled = useSelector(
    state => state.projectDetails.modules?.isOneClickLcaEnabled
  )

  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()

  const selectGlobalDateFunc = selectGlobalDateISO()
  const globalValue = useSelector(selectGlobalDateFunc)
  const navigate = useNavigate()

  const updateDateRange = useCallback(() => {
    // Set DateRange endDate to EndOfToday before redirecting so Report will show on Events Page.
    const newDateRange = { ...globalValue }
    let endOfToday = new Date().setHours(23, 59, 59, 999)
    endOfToday = new Date(endOfToday).toISOString()

    newDateRange.endDate = endOfToday
    dispatch(datePickerChanged(newDateRange, 'global'))
  }, [dispatch, globalValue])

  const onSuccess = useCallback(() => {
    updateDateRange()

    if (moduleName.toLowerCase() == 'delivery')
      navigate(
        generatePath(DELIVERY_EVENTS, {
          accountId: accountId,
          projectId: projectId,
          moduleName,
          tab: 'events'
        })
      )
    if (moduleName.toLowerCase() == 'waste')
      navigate(
        generatePath(WASTE_EVENTS, {
          accountId: accountId,
          projectId: projectId,
          moduleName,
          tab: 'events'
        })
      )
  }, [accountId, moduleName, navigate, projectId, updateDateRange])

  const exportRecords = useCallback(() => {
    dispatch(
      queueExport(
        accountId,
        projectId,
        moduleName,
        title,
        {
          startDate: startDate,
          endDate: endDate
        },
        filters,
        { onSuccess }
      )
    )

    trackDataExported()
  }, [
    dispatch,
    accountId,
    projectId,
    moduleName,
    title,
    startDate,
    endDate,
    filters,
    onSuccess,
    trackDataExported
  ])

  const { isReadOnly } = useUserProjectRole(accountId, projectId)
  if (isReadOnly) {
    // Readers should not be able to export records
    return null
  }

  const tooltipText = (
    <p style={{ marginBottom: '0' }}>
      Contact your Customer Success Manager for more details.
    </p>
  )

  const tooltipStyles = {
    borderRadius: '5px',
    padding: '11px 13px',
    color: colours.CORE_NAVY,
    boxShadow: '0px 2px 8px 0px #00000038'
  }

  return (
    <ButtonContainer>
      {isOneClickLcaEnabled && (
        <OneClickExportButton filters={filters} onSuccess={onSuccess} />
      )}
      {!isOneClickLcaEnabled && moduleName === 'delivery' && (
        <Tooltip
          title={tooltipText}
          styles={{ body: tooltipStyles }}
          color={colours.WHITE}
          placement="top"
        >
          <div>
            <OneClickExportButton
              filters={filters}
              onSuccess={onSuccess}
              disabled={true}
            />
          </div>
        </Tooltip>
      )}
      <ExportButton
        id="ExportButton"
        onClick={exportRecords}
        disabled={working}
      >
        {!working && <icons.Download size={sizes.SMALL} />}
        {working && <icons.Loading size={sizes.SMALL} />}
        <Spacer.Fine />
        Export CSV
      </ExportButton>
      {error && (
        <Text color="error">An issue has occured while generating export</Text>
      )}
    </ButtonContainer>
  )
}

const ButtonContainer = styled(Flex.Col)`
  flex: 0 0 auto;
  flex-direction: row;
  align-items: flex-end;
  gap: 8px;
`
const ExportButton = styled(ColourButton.Action)`
  width: 7.5rem;
`

RecordsExport.propTypes = {
  title: PropTypes.string,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  trackDataExported: PropTypes.func
}
